import { type ReactElement, useEffect, useState } from 'react';
import {
  SUPPORT_DESK_CHANNELS,
  SUPPORT_SUBJECT_KINDS,
  SUPPORT_TICKET_CATEGORIES,
  type SupportDeskChannel,
  type SupportSubjectCardView,
  type SupportSubjectKind,
  type SupportTicketCategory,
} from '@vello/api-client';
import { Button, Drawer, Field, Notice, Select, TextInput, DrawerHeader, DrawerBody, DrawerFooter } from '@vello/web-theme';
import { subjectQueryHintId } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { SubjectCard } from './subject-card';
import { DrawerStack } from './drawer-stack';

/**
 * Open a ticket for a caller (W5-SUPPORT-DESK, D384): the agent picks the channel and the category, says who the call is
 * about (a rider, a driver, a ride or a partner-desk order) by a handle the caller gives, confirms the SUBJECT CARD the
 * server finds in this market (only the support projection — never an email, document or address), then opens it. A
 * subject of another market is simply "not found".
 */
export function OpenTicketDrawer({ open, market, onClose, onOpened }: { open: boolean; market: string | undefined; onClose: () => void; onOpened: (ticketId: string) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail, v } = useVocab();
  const [channel, setChannel] = useState<SupportDeskChannel>('PHONE');
  const [category, setCategory] = useState<SupportTicketCategory>('TRIP');
  const [kind, setKind] = useState<SupportSubjectKind>('RIDE');
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ id: string; card: SupportSubjectCardView } | undefined>(undefined);
  const [message, setMessage] = useState<{ tone: 'danger' | 'warning' | 'neutral'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setFound(undefined);
    setMessage(undefined);
  }, [open]);

  const find = async (): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setFound(undefined);
    setMessage(undefined);
    const res = await runtime.supportDesk.lookupSubject({ market, kind, q: query.trim() });
    setBusy(false);
    if (res.ok) setFound({ id: res.value.subject.id, card: res.value.card });
    else if (res.error.code === 'NOT_FOUND') setMessage({ tone: 'warning', text: t('desk.new.notFound') });
    // D384 review (W5S-03): every lookup is recorded and budgeted per agent — say so instead of a bare code.
    else if (res.error.code === 'RATE_LIMITED') setMessage({ tone: 'warning', text: t('desk.new.rateLimited') });
    else setMessage({ tone: 'danger', text: t('desk.new.lookupFailed', { code: fail(res.error) }) });
  };

  const openTicket = async (): Promise<void> => {
    if (market === undefined || found === undefined) return;
    setBusy(true);
    const res = await runtime.supportDesk.openForCaller({ market, channel, category, subject: { kind, id: found.id } });
    setBusy(false);
    if (res.ok) onOpened(res.value.ticket.ticketId);
    else setMessage({ tone: 'danger', text: t('desk.new.failed', { code: fail(res.error) }) });
  };

  return (
    <Drawer open={open} onClose={onClose} labelledBy="desk-new-title">
      <DrawerHeader titleId="desk-new-title" title={t('desk.new.title')} subtitle={t('desk.new.subtitle')} />
      <DrawerBody>
        <DrawerStack gap={'var(--space-3)'}>
          {market === undefined && <Notice tone="warning">{t('desk.new.needsMarket')}</Notice>}
          <Field label={t('desk.new.channel')} htmlFor="desk-new-channel">
            <Select id="desk-new-channel" value={channel} onChange={(e) => setChannel(e.target.value as SupportDeskChannel)}>
              {SUPPORT_DESK_CHANNELS.map((c) => <option key={c} value={c}>{v('support.support_channel', c)}</option>)}
            </Select>
          </Field>
          <Field label={t('desk.new.category')} htmlFor="desk-new-category">
            <Select id="desk-new-category" value={category} onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}>
              {SUPPORT_TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{v('support.support_category', c)}</option>)}
            </Select>
          </Field>
          <Field label={t('desk.new.kind')} htmlFor="desk-new-kind">
            <Select id="desk-new-kind" value={kind} onChange={(e) => { setKind(e.target.value as SupportSubjectKind); setFound(undefined); setMessage(undefined); }}>
              {SUPPORT_SUBJECT_KINDS.map((k) => <option key={k} value={k}>{v('support.ticket_subject_kind', k)}</option>)}
            </Select>
          </Field>
          <Field label={t('desk.new.query')} htmlFor="desk-new-query" hint={t(subjectQueryHintId(kind))}>
            <TextInput id="desk-new-query" value={query} onChange={(e) => { setQuery(e.target.value); setFound(undefined); }} autoComplete="off" />
          </Field>
          <div>
            <Button variant="secondary" icon="search" disabled={busy || market === undefined || query.trim() === ''} onClick={() => void find()}>{t('desk.new.find')}</Button>
          </div>
          {message !== undefined && <Notice tone={message.tone}>{message.text}</Notice>}
          {found !== undefined && <SubjectCard card={found.card} />}
        </DrawerStack>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" onClick={onClose}>{t('ui.cancel')}</Button>
        <span className="vello-grow" />
        <Button variant="primary" disabled={busy || found === undefined} onClick={() => void openTicket()}>{t('desk.new.confirm')}</Button>
      </DrawerFooter>
    </Drawer>
  );
}
